import React from 'react'
import { Box, Card as MuiCard, CardContent as MuiCardContent } from '@mui/material'
import { Badge } from '@/components/ui/badge'
import { Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import CardImage2 from '@/components/reading/CardImage2'
import cardItems from '@/constants/cards.json'

// CommonCard component with same styling as DirectAnswerCard
const CommonCard = ({ children, sx = {} }: { children: React.ReactNode; sx?: any }) => (
  <MuiCard
    sx={{
      bgcolor: 'rgba(255, 255, 255, 0.7)',
      backdropFilter: 'blur-sm',
      border: '0.5px solid rgba(201, 173, 167, 0.4)',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      transition: 'all 200ms',
      marginBottom: 8,
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      },
      ...sx
    }}
  >
    {children}
  </MuiCard>
)

interface CardInteraction {
  cards_involved: any[]
  interaction_description: string
}

interface CardInteractionsSectionProps {
  cardInteractions?: CardInteraction[]
  cards?: any[]
  onCardClick?: (card: any) => void
  cardDescriptions?: Record<string, string>
}

export default function CardInteractionsSection({
  cardInteractions = [],
  cards = [],
  onCardClick,
  cardDescriptions = {}
}: CardInteractionsSectionProps) {
  const { t } = useTranslation()

  // Helper function to get card by ID
  const getCardById = (id: number) => {
    return cardItems.cards.find((card: any) => card.id === id)
  }

  if (!cardInteractions || cardInteractions.length === 0) {
    return null
  }

  return (
    <section aria-labelledby="interactions-heading">
      <h2
        id="interactions-heading"
        className="text-lg font-['Playfair_Display'] text-[#2B2E4A] font-semibold mb-0 mt-0 m-0"
      >
        {t('readingPage.cardInteractions')}
      </h2>
      <Box sx={{ mb: 2 }}>
        {cardInteractions.map((interaction: CardInteraction, index: number) => (
          <article key={index}>
            <MuiCard
              sx={{

                border: 'none',
                boxShadow: 'none',
                m: 0,
                height: 'fit-content',
                minHeight: '280px',
                pb: 6
              }}
            >
              <div className="grid grid-cols-2 gap-8 pt-4">
                {interaction.cards_involved.map((cardItem: any, idx: number) => {
                  // Handle both string and object formats
                  const cardName =
                    typeof cardItem === 'string' ? cardItem : cardItem.name || cardItem.card_name
                  const complementaryCardInfo = cards.find((c: any) => c.id === cardItem.id)
                  const card = { ...getCardById(cardItem.id), ...complementaryCardInfo }

                  return (
                    <div
                      key={idx}
                      className="relative flex flex-col items-center"
                      style={{ cursor: onCardClick ? 'pointer' : 'default' }}
                    >
                      <Badge variant="outline" className="mt-2 text-primary border-none">
                        {t(card?.card_name || '')}
                      </Badge>
                      <CardImage2
                        id={card?.id || 0}
                        visible={true}
                        width={140}
                        reversed={card?.reversed || card?.flipped || false}
                        description={cardDescriptions[cardName]}
                        onCardClick={onCardClick ? () => onCardClick(card) : undefined}
                      />
                    </div>
                  )
                })}
              </div>
            </MuiCard>
            <CommonCard sx={{ mb: 6 }}>
              <MuiCardContent>
                <div className="flex flex-col gap-4">
                  <div className="flex items-start gap-3">
                    <Sparkles className="h-5 w-5 text-[#2B2E4A] mt-1 flex-shrink-0" />
                    <div className="flex flex-col gap-1">
                      {interaction.interaction_description
                        ?.split('.')
                        .filter((sentence: string) => sentence.trim())
                        .map((sentence: string, idx: number) => (
                          <p
                            key={idx}
                            className="text-md leading-relaxed break-words text-[#2B2E4A]/70 font-['Plus_Jakarta_Sans'] mb-5"
                          >
                            {sentence.trim()}.
                          </p>
                        ))}
                    </div>
                  </div>
                </div>
              </MuiCardContent>
            </CommonCard>
          </article>
        ))}
      </Box>
    </section>
  )
}
